import { Notify } from 'quasar'
import appConsoleService from './appConsoleService';
import AppEnvVariablesService from './appEnvService';

/**
 * AppNotificationService
 * Direct-style injection version (No external SCSS required)
 */
class _AppNotificationService {
  constructor() {
    // Inject custom internal styles once to target inner Quasar elements
    if (typeof document !== 'undefined') {
      const style = document.createElement('style');
      style.innerHTML = `
        .big-notif-message .q-notification__caption { font-size: 1rem !important; opacity: 0.8 !important; }
        .big-notif-message .q-notification__message { font-size: 1.2rem !important; line-height: 1.4 !important; }
        .big-notif-message .q-notification__icon { font-size: 36px !important; }
        .app-msg-notif .q-notification__avatar { width: 40px; height: 40px; border-radius: 10px; background: #fff; padding: 3px; }
        .app-msg-notif .q-notification__message { flex: 1; }
        .app-msg-notif__row { display: flex; align-items: center; gap: 14px; }
        .app-msg-notif__text { flex: 1; min-width: 0; }
        .app-msg-notif__name { font-weight: 700; }
        .app-msg-notif__body { font-size: 1rem; opacity: 0.85; white-space: pre-line; word-break: break-word; }
        .app-msg-notif__avatar { width: 48px; height: 48px; border-radius: 50%; object-fit: cover; flex: none; box-shadow: 0 0 0 2px rgba(255,255,255,.7); }
        .app-msg-notif__avatar--initial { display: inline-flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1.3rem; background: rgba(255,255,255,.22); }
        @media (max-width: 599px) { .big-notif-message { min-width: 0 !important; width: calc(100vw - 24px); } }
      `;
      document.head.appendChild(style);
    }
    // Préférence « son des notifications » (persistée), activée par défaut.
    this._soundEnabled = this._readSoundPref();
  }

  /**
   * Serveur injoignable (piloté par le disjoncteur d'axiosService) : les toasts d'erreur sont
   * coupés — chaque requête et chaque service en échec en empilait un, alors que la fenêtre
   * « Maintenance en cours / Réessayer » suffit.
   */
  setConnectivityDown(down) {
    this._connectivityDown = !!down;
  }

  /** Ferme les toasts d'erreur encore affichés (bascule en fenêtre de maintenance). */
  dismissErrors() {
    (this._errorDismissers || []).forEach(dismiss => { try { dismiss(); } catch (e) { /* déjà fermé */ } });
    this._errorDismissers = [];
  }

  _readSoundPref() {
    try { return localStorage.getItem('app.notif.sound') !== '0'; } catch (e) { return true; }
  }

  /** Son des notifications activé ? (lu par _playSound, piloté par le panneau notif). */
  isSoundEnabled() { return this._soundEnabled !== false; }

  /** Active/désactive le son des notifications et persiste le choix. */
  setSoundEnabled(on) {
    this._soundEnabled = !!on;
    try { localStorage.setItem('app.notif.sound', on ? '1' : '0'); } catch (e) { /* storage indispo */ }
  }

  /**
   * Internal configuration for the "Big" look
   */
  _getBigConfig(type) {
    const base = {
      position: 'bottom-left',
      multiLine: true,
      timeout: 4000,
      classes: 'big-notif-message', // Hooks into the <style> injected above
      style: `
        min-width: 550px;
        padding: 24px;
        border-radius: 16px;
        box-shadow: 0 12px 24px rgba(0,0,0,0.3);
      `
    };

    const types = {
      info: {
        icon: 'success',
        color: 'blue-9',
        ...base
      },
      warning: {
        icon: 'warning',
        color: 'orange-10',
        ...base,
        timeout: 6000
      },
      error: {
        icon: 'report_problem',
        color: 'negative',
        ...base,
        timeout: 0,
        actions: [{ label: 'DISMISS', color: 'white', style: 'font-weight: bold; font-size: 1rem;' }]
      }
    };

    return types[type] || types.info;
  }

  /**
   * Push an Information message
   */
  pushInfo(message) {
    if (!message) return;
    appConsoleService.info(message);
    this._playSound('info');

    Notify.create({
      ...this._getBigConfig('info'),
      caption: message,
      message: 'Information',
      progress: true
    });
  }

  /**
   * Message d'un utilisateur (notification reçue), même disposition que la notification système sur Android :
   * logo de la marque à gauche, avatar de l'expéditeur à droite (initiale si pas de photo), nom puis texte.
   * Texte échappé (HTML du toast construit ici) ; même son que les informations.
   */
  pushMessage({sender, title, body}) {
    if (!title && !body) return;
    appConsoleService.info(`${title || ''} ${body || ''}`.trim());
    this._playSound('info');
    const esc = v => String(v ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
    const name = sender?.name || title || '';
    const right = sender?.image
      ? `<img class="app-msg-notif__avatar" src="${esc(sender.image)}" alt="">`
      : `<span class="app-msg-notif__avatar app-msg-notif__avatar--initial">${esc(name.trim().charAt(0).toUpperCase() || '?')}</span>`;
    const code = AppEnvVariablesService.getEnv('APP_CODE');
    const config = {
      ...this._getBigConfig('info'),
      classes: 'big-notif-message app-msg-notif',
      html: true,
      message: `<div class="app-msg-notif__row"><div class="app-msg-notif__text"><div class="app-msg-notif__name">${esc(name)}</div>`
        + (body ? `<div class="app-msg-notif__body">${esc(body)}</div>` : '') + `</div>${right}</div>`,
      progress: true
    };
    delete config.icon;
    if (code) config.avatar = `/${code}/icons/icon-192x192.png`;
    else config.icon = 'chat';
    Notify.create(config);
  }

  /**
   * Push a Warning message
   */
  pushWarning(message) {
    if (!message) return;
    this._playSound('warning');
    Notify.create({
      ...this._getBigConfig('warning'),
      caption: message,
      message: 'Warning',
    });
  }

  /**
   * Joue un son de notification synthétisé (Web Audio, aucun fichier à déployer) :
   * info = « Cristal » (ding aigu à longue résonance), warning = « Tom sourd » (percussion grave feutrée),
   * error = « Bonk » (note qui chute).
   * Uniquement au PREMIER PLAN : en arrière-plan c'est la push OS qui sonne
   * (évite le double). Échoue silencieusement si l'audio est bloqué/indispo.
   */
  _playSound(type) {
    try {
      if (!this.isSoundEnabled()) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!this._audioCtx) this._audioCtx = new Ctx();
      if (this._audioCtx.state === 'suspended') this._audioCtx.resume().catch(() => {});
      if (type === 'error') {
        this._tone(420, 0.3, 0.2, 140);
      } else if (type === 'warning') {
        this._tone(180, 0.35, 0.25, 70);
        this._tone(120, 0.35, 0.175, 55, 0.12);
      } else {
        // Cloche : fondamentale + partiels inharmoniques qui s'éteignent plus vite
        this._tone(1760, 1.4, 0.09);
        this._tone(1760 * 2.76, 0.56, 0.0225);
        this._tone(1760 * 5.4, 0.28, 0.009);
      }
    } catch (e) {
      /* audio indisponible */
    }
  }

  /**
   * Note sinusoïdale à attaque brève et extinction exponentielle ; `glideTo` = glissando jusqu'à cette
   * fréquence, `delay` = départ décalé (secondes).
   */
  _tone(freq, duration, gain, glideTo = null, delay = 0) {
    const ctx = this._audioCtx;
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t);
    if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, t + duration * 0.83);
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(gain, t + 0.005);
    env.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    osc.connect(env).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + duration + 0.05);
  }

  /**
   * Messages "humains" et rassurants par catégorie d'erreur. On ne montre
   * JAMAIS la stacktrace / le message technique brut à l'utilisateur final —
   * le détail technique reste dans la console (appConsoleService).
   */
  _friendly = {
    network: "Connexion au serveur impossible. Vérifie ta connexion internet et réessaie.",
    timeout: "Le serveur met un peu de temps à répondre. Réessaie dans un instant.",
    unauthorized: "Ta session a expiré. Reconnecte-toi pour continuer.",
    forbidden: "Tu n'as pas les droits nécessaires pour cette action.",
    notFound: "L'élément demandé est introuvable — il a peut-être été déplacé ou supprimé.",
    conflict: "Les données ont changé entre-temps. Recharge la page et réessaie.",
    tooLarge: "Le fichier est trop volumineux.",
    tooMany: "Un peu trop de demandes d'un coup. Patiente quelques secondes et réessaie.",
    invalid: "Certaines informations saisies sont invalides. Vérifie le formulaire et réessaie.",
    server: "Un incident technique est survenu de notre côté. Réessaie dans un instant — si ça persiste, contacte le support.",
    generic: "Une erreur est survenue. Réessaie — si le problème persiste, contacte le support.",
  };

  /**
   * Un message est "propre" (affichable tel quel) s'il est court et ne contient
   * aucun marqueur technique (HTML, stacktrace, SQL, exceptions, codes HTTP…).
   */
  _isClean(msg) {
    if (!msg || typeof msg !== 'string') return false;
    const m = msg.trim();
    if (m.length === 0 || m.length > 180) return false;
    const technical = /(<\/?[a-z!]|exception|stack|could not|resultset|\bsql\b|nullpointer|undefined|org\.|java\.|jakarta\.|hibernate|\bnull\b|status code|statuscode|econn|\bhttp\b|\bat [a-z].*\()/i;
    return !technical.test(m);
  }

  /** Convertit n'importe quelle erreur (objet axios ou string) en message humain. */
  _humanize(error) {
    if (typeof error === 'string') {
      return this._isClean(error) ? error : this._friendly.generic;
    }
    if (error && !error.response) {
      const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
      return isTimeout ? this._friendly.timeout : this._friendly.network;
    }
    const status = error?.response?.status;
    const backendMsg = error?.response?.data?.message;
    if (status === 401) return this._friendly.unauthorized;
    if (status === 403) return this._friendly.forbidden;
    if (status === 404) return this._friendly.notFound;
    if (status === 409) return this._friendly.conflict;
    if (status === 413) return this._friendly.tooLarge;
    if (status === 429) return this._friendly.tooMany;
    if (status >= 500) return this._friendly.server;
    if (status === 400 || status === 422) {
      return this._isClean(backendMsg) ? backendMsg : this._friendly.invalid;
    }
    // Autres statuts : on fait confiance au message backend s'il est propre.
    return this._isClean(backendMsg) ? backendMsg : this._friendly.generic;
  }

  /**
   * Push an Error message — message rassurant, dédoublonné (évite les doublons
   * intercepteur axios + catch des services), ton non alarmant.
   */
  pushError(error) {
    // Détail technique complet pour le debug — jamais montré à l'utilisateur.
    appConsoleService.error('appNotificationService', error);
    if (!error) return;
    // Serveur injoignable : la fenêtre de maintenance informe déjà l'utilisateur
    if (this._connectivityDown) return;

    const message = this._humanize(error);

    // Dédoublonnage : on évite d'empiler le même message en moins de 2 s.
    const now = (typeof Date !== 'undefined') ? Date.now() : 0;
    if (this._lastError && this._lastError.message === message && (now - this._lastError.ts) < 2000) {
      return;
    }
    this._lastError = { message, ts: now };
    this._playSound('error');

    const dismiss = Notify.create({
      ...this._getBigConfig('error'),
      icon: 'sentiment_dissatisfied',  // visage, plutôt qu'un triangle alarmant
      timeout: 8000,                    // disparaît tout seul (moins anxiogène)
      caption: message,                 // message humain, lisible
      message: 'Oups',
    });
    // Gardé pour dismissErrors (bascule en maintenance) ; liste bornée
    this._errorDismissers = [...(this._errorDismissers || []).slice(-9), dismiss];
  }
}

export default new _AppNotificationService();
